import React, {useEffect} from 'react';
import {History} from 'history';
import wrap from 'wraptile';
import {useHistory, useParams} from 'react-router-dom';
import {useForm, useField, FieldRenderProps} from 'react-final-form-hooks';
import {adminRoutes} from 'App/AppRouter/routes';
import ContentPartner from './ContentPartner';
import {
    ContentPartnerSaveHandlersProps,
    ContentPartnersResponse,
    ContentPartnerOnEffectCleanupProps,
    ContentPartnerOnEffectProps,
} from './ContentPartnersTypes';
import './ContentPartner.scss';

const MAX_STRING_LENGTH = 50;
const onSubmit = () => {};
const IS_REQUIRED = 'Field is required';
const EXCEED_LIMIT = 'Exceeded max limit';

interface ContentPartnerContainerProps {
    saveContentPartner: Function;
    setInitialValuesForEditingItem: Function;
    resetInitialValuesForEditingItem: Function;
    initialValues: ContentPartnersResponse;
}

export const pushRouteToHistory = wrap((history: History) =>
    history.push(adminRoutes.contentPartners));

export const setStatusBoolean = (id: boolean, value: string) => {
    if (!id) return true;
    return value === 'ACTIVE' || value === 'true';
};

export const saveHandler = wrap(
    ({
        saveContentPartner,
        values,
        onPushRouteToHistory,
        id,
    }: ContentPartnerSaveHandlersProps) => {
        saveContentPartner({
            id,
            body: {
                ...values,
                active: setStatusBoolean(Boolean(id), String(values.active)),
            },
            redirect: onPushRouteToHistory,
        });
    }
);

export const onEffectCleanup = wrap((handlers: ContentPartnerOnEffectCleanupProps) => {
    const {resetInitialValuesForEditingItem} = handlers;

    if (resetInitialValuesForEditingItem) resetInitialValuesForEditingItem();
});

export const onEffect = wrap((handlers: ContentPartnerOnEffectProps) => {
    const {
        id,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
    } = handlers;

    if (id) setInitialValuesForEditingItem(id);

    return onEffectCleanup({
        resetInitialValuesForEditingItem,
    });
});

export const validate = (values: ContentPartnersResponse) => {
    const {partnerName, cmsPartnerId} = values;
    const errors = {
        partnerName: '',
        cmsPartnerId: '',
    };

    if (!partnerName) {
        errors.partnerName = IS_REQUIRED;
    } else if (partnerName?.length > MAX_STRING_LENGTH) {
        errors.partnerName = EXCEED_LIMIT;
    }

    if (!cmsPartnerId) {
        errors.cmsPartnerId = IS_REQUIRED;
    } else if (cmsPartnerId?.length > MAX_STRING_LENGTH) {
        errors.cmsPartnerId = EXCEED_LIMIT;
    }
    return Object.values(errors).some(Boolean) ? errors : void 0;
};

export default function ContentPartnerContainer(props: ContentPartnerContainerProps) {
    const {
        saveContentPartner,
        initialValues: defaultValues,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
    } = props;
    const initialValues = {
        ...defaultValues,
        isValidDate: true,
    };
    const {form, valid} = useForm({
        onSubmit,
        validate,
        initialValues,
    });

    const history = useHistory();
    const {id} = useParams<{id: string}>();
    const {values} = form.getState();
    const onPushRouteToHistory = pushRouteToHistory(history);

    const setInitialValues = onEffect({
        id,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
    });

    useEffect(setInitialValues, []);

    const onSaveHandler = saveHandler({
        id,
        values,
        saveContentPartner,
        onPushRouteToHistory,
    });

    const partnerNameField: FieldRenderProps = useField('partnerName', form);
    const cmsPartnerIdField: FieldRenderProps = useField('cmsPartnerId', form);
    const statusField: FieldRenderProps = useField('active', form);
    const createdField: FieldRenderProps = useField('createdAt', form);
    const updatedField: FieldRenderProps = useField('updatedAt', form);

    return (
        <ContentPartner
            statusField={statusField}
            createdField={createdField}
            updatedField={updatedField}
            partnerNameField={partnerNameField}
            cmsPartnerIdField={cmsPartnerIdField}
            onSaveHandler={onSaveHandler}
            onPushRouteToHistory={onPushRouteToHistory}
            valid={valid}
            isEditMode={Boolean(id)}
        />
    );
}
